// src/pages/Consultation/StartSession.jsx - Enhanced Version with Guest Customer Input
import React, { useState, useEffect, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  FiArrowLeft,
  FiCalendar,
  FiUser,
  FiSearch,
  FiClock,
  FiPhone,
  FiMail,
  FiCheck,
  FiX,
  FiFilter,
  FiMapPin,
  FiInfo,
  FiChevronRight,
  FiUserX,
  FiZap,
  FiRefreshCw,
  FiAlertCircle,
} from "react-icons/fi";
import { startSessionThunk } from "../../store/thunks/consultationThunks";
import {
  getAppointmentDetailsThunk,
  getAppointmentsThunk,
} from "../../store/thunks/appointmentThunks";
import Button from "../../components/common/Button";
import Badge from "../../components/common/Badge";
import toast from "react-hot-toast";
import UserSelection from "pages/User/components/UserSelection";

const StartSession = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const searchInputRef = useRef(null);

  const { user } = useSelector((state) => state.auth);
  const { selectedBranch } = useSelector((state) => state.rate.ui);
  const { loading } = useSelector((state) => state.consultation);

  // Session type and steps
  const [currentStep, setCurrentStep] = useState(1);
  const [sessionType, setSessionType] = useState(null);

  // Form data with optional user
  const [formData, setFormData] = useState({
    sessionType: "",
    appointmentId: "",
    userId: "", // Optional
    isAnonymous: false, // New flag for anonymous sessions
    guestCustomer: {
      name: "",
      phoneNumber: "",
      email: "",
    },
    branchCode: selectedBranch || user?.branchAccess?.primaryBranch || "",
    storeCode: "",
    adminCode: user?.adminCode || user?._id || "",
    adminId: user?._id || "",
    adminName: user?.name || "",
    adminRole: user?.role || "",
    expectedDuration: "30",
    notes: "", // New field for session notes
    source: "ADMIN_DASHBOARD",
    channel: "IN_STORE",
  });

  // User selection state
  const [showUserSelection, setShowUserSelection] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [skipUserSelection, setSkipUserSelection] = useState(false);

  // Appointment selection state
  const [appointmentSearch, setAppointmentSearch] = useState("");
  const [appointmentSearchParams, setAppointmentSearchParams] = useState({});
  const [appointments, setAppointments] = useState([]);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [appointmentLoading, setAppointmentLoading] = useState(false);
  const [appointmentView, setAppointmentView] = useState("today");
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);

  // ✅ Auto-load from query parameters on mount
  useEffect(() => {
    const appointmentId = searchParams.get("appointmentId");
    const userId = searchParams.get("userId");
    const userPhone = searchParams.get("userPhone");
    const userName = searchParams.get("userName");
    const anonymous = searchParams.get("anonymous") === "true";

    if (appointmentId) {
      setSessionType("APPOINTMENT");
      setCurrentStep(2);
      loadAppointmentDetails(appointmentId);
    } else if (userId) {
      setSessionType("WALK_IN");
      setCurrentStep(2);
      setFormData((prev) => ({ ...prev, userId }));
      setCurrentStep(3); // Skip to details if user is provided
    } else if (anonymous) {
      setSessionType("WALK_IN");
      setSkipUserSelection(true);
      setFormData((prev) => ({ ...prev, isAnonymous: true }));
      setCurrentStep(3);
    } else if (userPhone || userName) {
      setSessionType("WALK_IN");
      setCurrentStep(2);
      // Will trigger user selection
    }
  }, [searchParams]);

  // Load appointment details
  const loadAppointmentDetails = async (appointmentId) => {
    try {
      setAppointmentLoading(true);
      const result = await dispatch(
        getAppointmentDetailsThunk(appointmentId)
      ).unwrap();

      if (result?.appointment) {
        handleAppointmentSelect(result.appointment);
        toast.success("Appointment loaded successfully");
      }
    } catch (error) {
      console.error("Error loading appointment:", error);
      toast.error(error?.message || "Failed to load appointment details");
    } finally {
      setAppointmentLoading(false);
    }
  };

  // Parse search query for parameters
  const parseSearchQuery = (query) => {
    const trimmed = query.trim();
    if (!trimmed || trimmed.length < 2) return { type: "none", params: {} };

    // Structured query: key=value pairs
    const paramPattern = /(\w+)=([^&\s]+)/g;
    const matches = [...trimmed.matchAll(paramPattern)];

    if (matches.length > 0) {
      const params = {};
      matches.forEach(([, key, value]) => {
        params[key] = value.trim();
      });
      return { type: "structured", params };
    }

    // Smart detection for unstructured queries
    const phonePattern = /^\d{10}$/;
    const codePattern = /^APT-/i;
    const cleanQuery = trimmed.replace(/\D/g, "");

    if (phonePattern.test(cleanQuery)) {
      return { type: "structured", params: { userPhone: cleanQuery } };
    } else if (codePattern.test(trimmed)) {
      return { type: "structured", params: { appointmentCode: trimmed } };
    } else {
      return { type: "structured", params: { userName: trimmed } };
    }
  };

  // Load appointments with better error handling
  const loadAppointments = async (
    view = appointmentView,
    customParams = null
  ) => {
    try {
      setAppointmentLoading(true);
      let result;
      let searchFilters = customParams || appointmentSearchParams;

      const baseFilters = {
        branchCode: selectedBranch,
        status: ["CONFIRMED", "PENDING", "CHECKED_IN"],
      };

      if (view === "search") {
        if (
          Object.keys(searchFilters).length > 0 ||
          appointmentSearch.length >= 2
        ) {
          if (!customParams && appointmentSearch) {
            const parsed = parseSearchQuery(appointmentSearch);
            searchFilters = parsed.params;
            setAppointmentSearchParams(searchFilters);
          }

          result = await dispatch(
            getAppointmentsThunk({
              ...baseFilters,
              ...searchFilters,
            })
          ).unwrap();
        }
      } else if (view === "assigned") {
        result = await dispatch(
          getAppointmentsThunk({
            ...baseFilters,
            adminCode: user?.adminCode || user?._id,
          })
        ).unwrap();
      } else if (view === "today") {
        const today = new Date().toISOString().split("T")[0];
        result = await dispatch(
          getAppointmentsThunk({
            ...baseFilters,
            date: today,
          })
        ).unwrap();
      }

      setAppointments(result?.appointments || []);
    } catch (error) {
      console.error("Error loading appointments:", error);
      toast.error(error?.message || "Failed to load appointments");
      setAppointments([]);
    } finally {
      setAppointmentLoading(false);
    }
  };

  // Search with debouncing
  useEffect(() => {
    if (appointmentView === "search" && appointmentSearch.length >= 2) {
      const timer = setTimeout(() => {
        const parsed = parseSearchQuery(appointmentSearch);
        if (Object.keys(parsed.params).length > 0) {
          setAppointmentSearchParams(parsed.params);
          loadAppointments("search", parsed.params);
        }
      }, 500);
      return () => clearTimeout(timer);
    } else if (appointmentSearch.length === 0) {
      setAppointmentSearchParams({});
      setAppointments([]);
    }
  }, [appointmentSearch]);

  // Load appointments when view changes
  useEffect(() => {
    if (
      currentStep === 2 &&
      sessionType === "APPOINTMENT" &&
      appointmentView !== "search"
    ) {
      loadAppointments(appointmentView);
    }
  }, [appointmentView, currentStep, sessionType]);

  // Advanced search handlers
  const handleAdvancedSearchChange = (field, value) => {
    setAppointmentSearchParams((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleApplyAdvancedSearch = () => {
    const cleanParams = Object.fromEntries(
      Object.entries(appointmentSearchParams).filter(([_, v]) => v !== "")
    );
    if (Object.keys(cleanParams).length > 0) {
      loadAppointments("search", cleanParams);
    } else {
      toast.error("Please enter at least one search parameter");
    }
  };

  const handleClearAdvancedSearch = () => {
    setAppointmentSearchParams({});
    setAppointmentSearch("");
    setAppointments([]);
    setShowAdvancedSearch(false);
  };

  // Handle type selection
  const handleSelectType = (type) => {
    setSessionType(type);
    setFormData((prev) => ({ ...prev, sessionType: type }));

    if (type === "WALK_IN") {
      setCurrentStep(2); // Go to user selection step
    } else if (type === "APPOINTMENT") {
      setCurrentStep(2);
      setAppointmentView("today");
    }
  };

  // Handle appointment selection
  const handleAppointmentSelect = (appointment) => {
    setSelectedAppointment(appointment);
    setFormData((prev) => ({
      ...prev,
      appointmentId: appointment._id,
      userId: appointment.userId || "",
      expectedDuration: appointment.duration?.toString() || "30",
      guestCustomer: {
        name: appointment.userName || "",
        phoneNumber: appointment.userPhone || "",
        email: appointment.userEmail || "",
      },
      isAnonymous: false,
    }));
    setCurrentStep(3);
    toast.success(`Selected: ${appointment.userName}`);
  };

  // Handle user selection
  const handleUserSelected = (user) => {
    setSelectedUser(user);
    setSkipUserSelection(false);
    setFormData((prev) => ({
      ...prev,
      userId: user._id || user.customerId,
      guestCustomer: {
        name: user.name || "",
        phoneNumber: user.phoneNumber || "",
        email: user.email || "",
      },
      isAnonymous: false,
    }));
    setShowUserSelection(false);
    setCurrentStep(3);
    toast.success(`Selected: ${user.name}`);
  };

  // ✅ NEW: Skip user selection (anonymous session)
  const handleSkipUserSelection = () => {
    setSkipUserSelection(true);
    setSelectedUser(null);
    setFormData((prev) => ({
      ...prev,
      userId: "",
      isAnonymous: true,
      guestCustomer: { name: "", phoneNumber: "", email: "" },
    }));
    setShowUserSelection(false);
    setCurrentStep(3);
    toast.success("Starting anonymous session");
  };

  // Handle form change
  const handleChange = (e) => {
    const { name, value } = e.target;

    // Special handling for nested guestCustomer object
    if (name === "guestCustomer") {
      setFormData((prev) => ({ ...prev, guestCustomer: value }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  // ✅ IMPROVED: Validation with optional user
  const validateForm = () => {
    if (!sessionType) {
      toast.error("Please select a session type");
      return false;
    }

    if (sessionType === "APPOINTMENT" && !formData.appointmentId) {
      toast.error("Please select an appointment");
      return false;
    }

    // User is now optional - no validation needed for walk-ins
    if (!formData.expectedDuration) {
      toast.error("Please select expected duration");
      return false;
    }

    return true;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      const sessionData = {
        ...formData,
        sessionType,
        // Only include userId if it exists
        ...(formData.userId && { userId: formData.userId }),
        // Clean up empty guest customer fields
        guestCustomer: {
          name: formData.guestCustomer.name?.trim() || "",
          phoneNumber: formData.guestCustomer.phoneNumber?.trim() || "",
          email: formData.guestCustomer.email?.trim() || "",
        },
      };

      const result = await dispatch(startSessionThunk(sessionData)).unwrap();

      if (result?.data?.session) {
        toast.success("Session started successfully! 🎉");
        navigate(`/consultation/sessions/${result.data.session._id}`);
      } else {
        throw new Error("Invalid response format");
      }
    } catch (error) {
      console.error("Start session error:", error);
      toast.error(error?.message || "Failed to start session");
    }
  };

  // Reset to step 1
  const handleReset = () => {
    setSessionType(null);
    setCurrentStep(1);
    setSelectedAppointment(null);
    setSelectedUser(null);
    setSkipUserSelection(false);
    setAppointmentSearch("");
    setAppointmentSearchParams({});
    setAppointments([]);
    setFormData((prev) => ({
      ...prev,
      sessionType: "",
      appointmentId: "",
      userId: "",
      isAnonymous: false,
      guestCustomer: { name: "", phoneNumber: "", email: "" },
      expectedDuration: "30",
      notes: "",
    }));
  };

  // Format date/time
  const formatDateTime = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleString("en-IN", {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Get status color
  const getStatusColor = (status) => {
    const colors = {
      PENDING: "warning",
      CONFIRMED: "success",
      CHECKED_IN: "info",
    };
    return colors[status] || "default";
  };

  // Memoized appointment stats
  const appointmentStats = useMemo(() => {
    if (appointmentView !== "search" && appointments.length > 0) {
      const confirmed = appointments.filter(
        (a) => a.status === "CONFIRMED"
      ).length;
      const pending = appointments.filter((a) => a.status === "PENDING").length;
      const checkedIn = appointments.filter(
        (a) => a.status === "CHECKED_IN"
      ).length;
      return { confirmed, pending, checkedIn, total: appointments.length };
    }
    return null;
  }, [appointments, appointmentView]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e) => {
      // ESC to go back or close
      if (e.key === "Escape") {
        if (currentStep > 1) {
          setCurrentStep((prev) => prev - 1);
        } else {
          navigate("/consultation");
        }
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [currentStep, navigate]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() =>
                  currentStep > 1
                    ? setCurrentStep(currentStep - 1)
                    : navigate("/consultation")
                }
                className="text-gray-600 hover:text-gray-900 transition-colors"
                title="Go back (ESC)"
              >
                <FiArrowLeft size={20} />
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">
                  Start New Session
                </h1>
                <p className="text-sm text-gray-500 mt-0.5">
                  {sessionType === "APPOINTMENT"
                    ? "From scheduled appointment"
                    : sessionType === "WALK_IN"
                    ? skipUserSelection
                      ? "Anonymous walk-in"
                      : "Walk-in customer"
                    : "Choose how to start"}
                </p>
              </div>
            </div>

            {/* Progress Steps */}
            <div className="hidden md:flex items-center gap-2">
              {[
                { num: 1, label: "Type" },
                {
                  num: 2,
                  label: sessionType === "APPOINTMENT" ? "Appt" : "Customer",
                },
                { num: 3, label: "Details" },
              ].map(({ num, label }, idx) => (
                <React.Fragment key={num}>
                  <div className="flex flex-col items-center">
                    <div
                      className={`flex items-center justify-center w-10 h-10 rounded-full transition-all ${
                        currentStep >= num
                          ? "bg-primary text-white shadow-md"
                          : "bg-gray-200 text-gray-500"
                      }`}
                    >
                      {currentStep > num ? <FiCheck size={18} /> : num}
                    </div>
                    <span className="text-xs mt-1 text-gray-600">{label}</span>
                  </div>
                  {idx < 2 && (
                    <FiChevronRight
                      className={`mb-5 ${
                        currentStep > num ? "text-primary" : "text-gray-300"
                      }`}
                    />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-6">
        <div className="max-w-6xl mx-auto">
          {/* Step 1: Select Session Type */}
          {currentStep === 1 && (
            <div className="space-y-6 animate-fadeIn">
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  How would you like to start?
                </h2>
                <p className="text-gray-600">
                  Choose between scheduled appointment or walk-in customer
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
                {/* Appointment Option */}
                <button
                  onClick={() => handleSelectType("APPOINTMENT")}
                  className="bg-white p-8 rounded-xl border-2 border-gray-200 hover:border-primary hover:shadow-lg transition-all text-left group"
                >
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-primary/10 rounded-lg group-hover:bg-primary/20 transition-colors">
                      <FiCalendar className="text-primary text-2xl" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        From Appointment
                      </h3>
                      <p className="text-gray-600 mb-4">
                        Start session from a pre-scheduled appointment with all
                        details ready.
                      </p>
                      <ul className="space-y-2 text-sm text-gray-500">
                        <li className="flex items-center gap-2">
                          <FiCheck className="text-green-500 flex-shrink-0" />
                          Customer details pre-filled
                        </li>
                        <li className="flex items-center gap-2">
                          <FiCheck className="text-green-500 flex-shrink-0" />
                          Intent and preferences known
                        </li>
                        <li className="flex items-center gap-2">
                          <FiCheck className="text-green-500 flex-shrink-0" />
                          Expected duration set
                        </li>
                      </ul>
                    </div>
                  </div>
                </button>

                {/* Walk-in Option */}
                <button
                  onClick={() => handleSelectType("WALK_IN")}
                  className="bg-white p-8 rounded-xl border-2 border-gray-200 hover:border-primary hover:shadow-lg transition-all text-left group"
                >
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-blue-50 rounded-lg group-hover:bg-blue-100 transition-colors">
                      <FiUser className="text-blue-600 text-2xl" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        Walk-in Customer
                      </h3>
                      <p className="text-gray-600 mb-4">
                        Quick session for customers who just walked in. Flexible
                        and fast.
                      </p>
                      <ul className="space-y-2 text-sm text-gray-500">
                        <li className="flex items-center gap-2">
                          <FiCheck className="text-green-500 flex-shrink-0" />
                          Immediate service
                        </li>
                        <li className="flex items-center gap-2">
                          <FiCheck className="text-green-500 flex-shrink-0" />
                          Optional customer selection
                        </li>
                        <li className="flex items-center gap-2">
                          <FiCheck className="text-green-500 flex-shrink-0" />
                          Anonymous sessions allowed
                        </li>
                      </ul>
                    </div>
                  </div>
                </button>
              </div>

              {/* Quick Stats or Recent Sessions */}
              <div className="mt-8 p-6 bg-gradient-to-r from-primary-light to-blue-50 rounded-lg border border-primary/20">
                <div className="flex items-start gap-3">
                  <FiZap className="text-white text-xl mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-white mb-1">
                      Quick Start Tips
                    </h4>
                    <p className="text-sm text-white">
                      • Press{" "}
                      <kbd className="px-2 py-0.5 bg-gray-500 border rounded text-xs">
                        ESC
                      </kbd>{" "}
                      to go back
                      <br />
                      • Customer selection is optional for walk-ins
                      <br />• You can link customers later from the session page
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Select Appointment */}
          {currentStep === 2 && sessionType === "APPOINTMENT" && (
            <AppointmentSelection
              appointmentView={appointmentView}
              setAppointmentView={setAppointmentView}
              appointmentSearch={appointmentSearch}
              setAppointmentSearch={setAppointmentSearch}
              appointmentSearchParams={appointmentSearchParams}
              showAdvancedSearch={showAdvancedSearch}
              setShowAdvancedSearch={setShowAdvancedSearch}
              handleAdvancedSearchChange={handleAdvancedSearchChange}
              handleApplyAdvancedSearch={handleApplyAdvancedSearch}
              handleClearAdvancedSearch={handleClearAdvancedSearch}
              appointmentLoading={appointmentLoading}
              appointments={appointments}
              appointmentStats={appointmentStats}
              handleAppointmentSelect={handleAppointmentSelect}
              handleReset={handleReset}
              formatDateTime={formatDateTime}
              getStatusColor={getStatusColor}
              searchInputRef={searchInputRef}
            />
          )}

          {/* Step 2: Walk-in Customer Selection */}
          {currentStep === 2 && sessionType === "WALK_IN" && (
            <div className="space-y-6 animate-fadeIn">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">
                    Customer Selection
                  </h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Select an existing customer or skip for anonymous session
                  </p>
                </div>
                <Button variant="outline" size="sm" onClick={handleReset}>
                  <FiX className="mr-1" /> Change Type
                </Button>
              </div>

              {/* Options Card */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
                {/* Select Customer */}
                <button
                  onClick={() => setShowUserSelection(true)}
                  className="bg-white p-8 rounded-xl border-2 border-gray-200 hover:border-primary hover:shadow-lg transition-all text-left group"
                >
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-blue-50 rounded-lg group-hover:bg-blue-100">
                      <FiUser className="text-blue-600 text-2xl" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        Select Customer
                      </h3>
                      <p className="text-gray-600 mb-4">
                        Search existing customers or create new customer profile
                      </p>
                      <ul className="space-y-2 text-sm text-gray-500">
                        <li className="flex items-center gap-2">
                          <FiCheck className="text-green-500" />
                          Access customer history
                        </li>
                        <li className="flex items-center gap-2">
                          <FiCheck className="text-green-500" />
                          Track preferences
                        </li>
                        <li className="flex items-center gap-2">
                          <FiCheck className="text-green-500" />
                          Build customer profile
                        </li>
                      </ul>
                    </div>
                  </div>
                </button>

                {/* Skip Customer Selection */}
                <button
                  onClick={handleSkipUserSelection}
                  className="bg-white p-8 rounded-xl border-2 border-gray-200 hover:border-primary hover:shadow-lg transition-all text-left group"
                >
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-purple-50 rounded-lg group-hover:bg-purple-100">
                      <FiUserX className="text-purple-600 text-2xl" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        Anonymous Session
                      </h3>
                      <p className="text-gray-600 mb-4">
                        Quick session without customer selection. Link later if
                        needed.
                      </p>
                      <ul className="space-y-2 text-sm text-gray-500">
                        <li className="flex items-center gap-2">
                          <FiZap className="text-purple-500" />
                          Fastest start
                        </li>
                        <li className="flex items-center gap-2">
                          <FiZap className="text-purple-500" />
                          Optional guest details
                        </li>
                        <li className="flex items-center gap-2">
                          <FiZap className="text-purple-500" />
                          Link customer later
                        </li>
                      </ul>
                    </div>
                  </div>
                </button>
              </div>

              <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg max-w-4xl mx-auto">
                <FiInfo className="text-blue-600 text-lg mt-0.5 flex-shrink-0" />
                <p className="text-sm text-blue-800">
                  <strong>Tip:</strong> You can always link a customer to the
                  session later from the session page. Anonymous sessions are
                  great for quick interactions where customer details aren't
                  immediately available.
                </p>
              </div>
            </div>
          )}

          {/* Step 3: Session Details & Confirmation */}
          {currentStep === 3 && (
            <SessionDetails
              sessionType={sessionType}
              selectedAppointment={selectedAppointment}
              selectedUser={selectedUser}
              skipUserSelection={skipUserSelection}
              formData={formData}
              handleChange={handleChange}
              handleSubmit={handleSubmit}
              loading={loading}
              setCurrentStep={setCurrentStep}
              formatDateTime={formatDateTime}
              getStatusColor={getStatusColor}
              handleReset={handleReset}
            />
          )}
        </div>
      </div>

      {/* User Selection Modal */}
      {showUserSelection && (
        <UserSelection
          isOpen={showUserSelection}
          onClose={() => {
            setShowUserSelection(false);
          }}
          onUserSelected={handleUserSelected}
          mode="smart"
          title="Select Customer for Walk-in Session"
          subtitle="Search existing customers or create new profile"
          showEmail={true}
          showAlternatePhone={false}
          showAddress={false}
          showKYC={false}
          showGSTIN={false}
          allowSearch={true}
          allowCreate={true}
          allowEdit={false}
          autoFillFromSearch={true}
        />
      )}
    </div>
  );
};

// ============================================
// APPOINTMENT SELECTION COMPONENT
// ============================================
const AppointmentSelection = ({
  appointmentView,
  setAppointmentView,
  appointmentSearch,
  setAppointmentSearch,
  appointmentSearchParams,
  showAdvancedSearch,
  setShowAdvancedSearch,
  handleAdvancedSearchChange,
  handleApplyAdvancedSearch,
  handleClearAdvancedSearch,
  appointmentLoading,
  appointments,
  appointmentStats,
  handleAppointmentSelect,
  handleReset,
  formatDateTime,
  getStatusColor,
  searchInputRef,
}) => {
  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-bold text-gray-900">
            Select Appointment
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Search or browse appointments to start session
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={handleReset}>
          <FiX className="mr-1" /> Change Type
        </Button>
      </div>

      {/* View Tabs with Stats */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setAppointmentView("today")}
            className={`flex-1 px-6 py-3 text-sm font-medium transition-colors ${
              appointmentView === "today"
                ? "text-primary border-b-2 border-primary bg-primary/5"
                : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
            }`}
          >
            <FiCalendar className="inline mr-2" />
            Today
          </button>
          <button
            onClick={() => setAppointmentView("assigned")}
            className={`flex-1 px-6 py-3 text-sm font-medium transition-colors ${
              appointmentView === "assigned"
                ? "text-primary border-b-2 border-primary bg-primary/5"
                : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
            }`}
          >
            <FiUser className="inline mr-2" />
            My Appointments
          </button>
          <button
            onClick={() => setAppointmentView("search")}
            className={`flex-1 px-6 py-3 text-sm font-medium transition-colors ${
              appointmentView === "search"
                ? "text-primary border-b-2 border-primary bg-primary/5"
                : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
            }`}
          >
            <FiSearch className="inline mr-2" />
            Search
          </button>
        </div>

        {/* Stats Bar */}
        {appointmentStats && (
          <div className="px-6 py-3 bg-gray-50 border-b border-gray-200">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">
                Total: <strong>{appointmentStats.total}</strong>
              </span>
              <div className="flex gap-4">
                <span className="text-green-600">
                  Confirmed: <strong>{appointmentStats.confirmed}</strong>
                </span>
                <span className="text-yellow-600">
                  Pending: <strong>{appointmentStats.pending}</strong>
                </span>
                <span className="text-blue-600">
                  Checked-in: <strong>{appointmentStats.checkedIn}</strong>
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Search Section */}
        {appointmentView === "search" && (
          <div className="p-4 border-b border-gray-200 bg-gray-50">
            <div className="flex gap-2 mb-2">
              <div className="relative flex-1">
                <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={appointmentSearch}
                  onChange={(e) => setAppointmentSearch(e.target.value)}
                  placeholder="Search by name, phoneNumber, or appointment code..."
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  autoFocus
                />
              </div>
              <Button
                variant="outline"
                onClick={() => setShowAdvancedSearch(!showAdvancedSearch)}
              >
                <FiFilter className="mr-1" />
                {showAdvancedSearch ? "Hide" : "Filters"}
              </Button>
            </div>

            {!showAdvancedSearch && (
              <p className="text-xs text-gray-500">
                💡 Try: "9876543210" or "Rahul" or "APT-12345"
              </p>
            )}

            {/* Advanced Search Form */}
            {showAdvancedSearch && (
              <div className="mt-3 p-4 bg-white rounded-lg border border-gray-200">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <input
                    type="text"
                    placeholder="Customer Name"
                    value={appointmentSearchParams.userName || ""}
                    onChange={(e) =>
                      handleAdvancedSearchChange("userName", e.target.value)
                    }
                    className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                  <input
                    type="text"
                    placeholder="Phone Number"
                    value={appointmentSearchParams.userPhone || ""}
                    onChange={(e) =>
                      handleAdvancedSearchChange("userPhone", e.target.value)
                    }
                    maxLength="10"
                    className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                  <input
                    type="text"
                    placeholder="Appointment Code"
                    value={appointmentSearchParams.appointmentCode || ""}
                    onChange={(e) =>
                      handleAdvancedSearchChange(
                        "appointmentCode",
                        e.target.value
                      )
                    }
                    className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                  <input
                    type="date"
                    value={appointmentSearchParams.date || ""}
                    onChange={(e) =>
                      handleAdvancedSearchChange("date", e.target.value)
                    }
                    className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                  <select
                    value={appointmentSearchParams.status || ""}
                    onChange={(e) =>
                      handleAdvancedSearchChange("status", e.target.value)
                    }
                    className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="">All Status</option>
                    <option value="CONFIRMED">Confirmed</option>
                    <option value="PENDING">Pending</option>
                    <option value="CHECKED_IN">Checked In</option>
                  </select>
                  <select
                    value={appointmentSearchParams.type || ""}
                    onChange={(e) =>
                      handleAdvancedSearchChange("type", e.target.value)
                    }
                    className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="">All Types</option>
                    <option value="PRODUCT_CONSULTATION">
                      Product Consultation
                    </option>
                    <option value="CUSTOM_DESIGN">Custom Design</option>
                    <option value="WEDDING_JEWELRY">Wedding Jewelry</option>
                  </select>
                </div>
                <div className="flex justify-end gap-2 mt-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleClearAdvancedSearch}
                  >
                    Clear All
                  </Button>
                  <Button size="sm" onClick={handleApplyAdvancedSearch}>
                    <FiSearch className="mr-1" /> Apply Filters
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Appointments List */}
        <div className="p-4 min-h-[400px]">
          {appointmentLoading ? (
            <div className="flex flex-col justify-center items-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
              <p className="text-gray-500 text-sm">Loading appointments...</p>
            </div>
          ) : appointments.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {appointments.map((appointment) => (
                <AppointmentCard
                  key={appointment._id}
                  appointment={appointment}
                  onSelect={() => handleAppointmentSelect(appointment)}
                  formatDateTime={formatDateTime}
                  getStatusColor={getStatusColor}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <FiCalendar className="mx-auto text-6xl text-gray-300 mb-4" />
              <p className="text-gray-600 font-medium mb-2">
                {appointmentView === "search" && appointmentSearch.length < 2
                  ? "Start typing to search"
                  : "No appointments found"}
              </p>
              <p className="text-sm text-gray-500">
                {appointmentView === "search" && appointmentSearch.length >= 2
                  ? "Try adjusting your search or filters"
                  : appointmentView === "today"
                  ? "No appointments scheduled for today"
                  : "No appointments assigned to you"}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================
// SESSION DETAILS COMPONENT
// ============================================
const SessionDetails = ({
  sessionType,
  selectedAppointment,
  selectedUser,
  skipUserSelection,
  formData,
  handleChange,
  handleSubmit,
  loading,
  setCurrentStep,
  formatDateTime,
  getStatusColor,
  handleReset,
}) => {
  // Handle guest customer input changes
  const handleGuestCustomerChange = (field, value) => {
    handleChange({
      target: {
        name: "guestCustomer",
        value: {
          ...formData.guestCustomer,
          [field]: value,
        },
      },
    });
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 animate-fadeIn">
      {/* Left: Selected Customer/Appointment + Form */}
      <div className="lg:col-span-2 space-y-6">
        {/* Selected Entity Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-start justify-between mb-4">
            <h3 className="font-semibold text-gray-900">
              {sessionType === "APPOINTMENT"
                ? "Selected Appointment"
                : skipUserSelection
                ? "Anonymous Session"
                : "Selected Customer"}
            </h3>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentStep(2)}
            >
              <FiRefreshCw className="mr-1" size={14} /> Change
            </Button>
          </div>

          {sessionType === "APPOINTMENT" && selectedAppointment ? (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-green-50 rounded-lg">
                  <FiCalendar className="text-green-600 text-xl" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-gray-900 text-lg">
                    {selectedAppointment.userName}
                  </p>
                  <Badge
                    variant={getStatusColor(selectedAppointment.status)}
                    size="sm"
                    className="mt-1"
                  >
                    {selectedAppointment.status}
                  </Badge>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                <div className="flex items-center gap-2 text-sm">
                  <FiPhone className="text-gray-400" />
                  <span className="text-gray-700">
                    {selectedAppointment.userPhone}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <FiClock className="text-gray-400" />
                  <span className="text-gray-700">
                    {formatDateTime(selectedAppointment.scheduledDateTime)}
                  </span>
                </div>
                {selectedAppointment.userEmail && (
                  <div className="col-span-2 flex items-center gap-2 text-sm">
                    <FiMail className="text-gray-400" />
                    <span className="text-gray-700">
                      {selectedAppointment.userEmail}
                    </span>
                  </div>
                )}
                <div className="col-span-2 pt-3 border-t">
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">
                    Appointment Type
                  </p>
                  <p className="text-sm font-medium text-gray-900">
                    {selectedAppointment.type?.replace(/_/g, " ")}
                  </p>
                </div>
              </div>
            </div>
          ) : sessionType === "WALK_IN" && skipUserSelection ? (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-purple-50 rounded-lg">
                  <FiUserX className="text-purple-600 text-xl" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-gray-900 text-lg">
                    Anonymous Customer
                  </p>
                  <p className="text-sm text-gray-600 mt-1">
                    Quick session without customer profile
                  </p>
                </div>
              </div>

              {/* ✅ Guest Customer Input Fields - NEW */}
              <div className="pt-4 border-t space-y-4">
                <p className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <FiInfo className="text-blue-500" />
                  Guest Customer Details{" "}
                  <span className="text-gray-400 font-normal">(Optional)</span>
                </p>

                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1.5">
                      Guest Name
                    </label>
                    <input
                      type="text"
                      value={formData.guestCustomer.name}
                      onChange={(e) =>
                        handleGuestCustomerChange("name", e.target.value)
                      }
                      placeholder="Enter guest name (optional)"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1.5">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      value={formData.guestCustomer.phoneNumber}
                      onChange={(e) =>
                        handleGuestCustomerChange(
                          "phoneNumber",
                          e.target.value.replace(/\D/g, "")
                        )
                      }
                      placeholder="10-digit phoneNumber number (optional)"
                      maxLength="10"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1.5">
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={formData.guestCustomer.email}
                      onChange={(e) =>
                        handleGuestCustomerChange("email", e.target.value)
                      }
                      placeholder="email@example.com (optional)"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <FiInfo className="text-blue-600 text-sm mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-blue-800">
                      These details help track the session but won't create a
                      customer profile. You can link to an existing customer
                      later if needed.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ) : sessionType === "WALK_IN" &&
            (selectedUser || formData.guestCustomer.name) ? (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-blue-50 rounded-lg">
                  <FiUser className="text-blue-600 text-xl" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-gray-900 text-lg">
                    {selectedUser?.name || formData.guestCustomer.name}
                  </p>
                  {selectedUser?.status === "accepted" && (
                    <Badge variant="success" size="sm" className="mt-1">
                      Active Customer
                    </Badge>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                <div className="flex items-center gap-2 text-sm">
                  <FiPhone className="text-gray-400" />
                  <span className="text-gray-700">
                    {selectedUser?.phoneNumber ||
                      formData.guestCustomer.phoneNumber}
                  </span>
                </div>
                {(selectedUser?.email || formData.guestCustomer.email) && (
                  <div className="flex items-center gap-2 text-sm">
                    <FiMail className="text-gray-400" />
                    <span className="text-gray-700">
                      {selectedUser?.email || formData.guestCustomer.email}
                    </span>
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </div>

        {/* Session Details Form */}
        <form
          onSubmit={handleSubmit}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
        >
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <FiClock className="text-primary" />
            Session Configuration
          </h3>

          <div className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Branch Location
              </label>
              <div className="flex items-center gap-3 px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg">
                <FiMapPin className="text-gray-400 text-lg" />
                <span className="text-gray-900 font-medium">
                  {formData.branchCode}
                </span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Expected Duration <span className="text-red-500">*</span>
              </label>
              <select
                name="expectedDuration"
                value={formData.expectedDuration}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                required
              >
                <option value="15">15 minutes - Quick browse</option>
                <option value="30">30 minutes - Standard</option>
                <option value="60">1 hour - Detailed consultation</option>
                <option value="90">1.5 hours - Extended session</option>
                <option value="120">2 hours - In-depth planning</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Helps with scheduling and resource allocation
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Session Notes <span className="text-gray-400">(Optional)</span>
              </label>
              <textarea
                name="notes"
                value={formData.notes || ""}
                onChange={handleChange}
                placeholder="Add any initial notes or context about this session..."
                rows="3"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all resize-none"
              />
            </div>

            <div className="pt-4 border-t">
              <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <FiInfo className="text-blue-600 text-lg mt-0.5 flex-shrink-0" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium mb-1">What happens next?</p>
                  <ul className="space-y-1 list-disc list-inside">
                    <li>Track products shown to customer</li>
                    <li>Add items to cart during consultation</li>
                    <li>Convert to order when ready</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div className="flex gap-3 mt-6 pt-6 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => setCurrentStep(2)}
              className="flex-1"
            >
              <FiArrowLeft className="mr-1" size={16} /> Back
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="flex-1 relative"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Starting...
                </>
              ) : (
                <>
                  <FiCheck className="mr-1" size={16} /> Start Session
                </>
              )}
            </Button>
          </div>
        </form>
      </div>

      {/* Right: Summary + Tips */}
      <div className="space-y-6">
        {/* Summary Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Session Summary</h3>

          <div className="space-y-3">
            <SummaryRow
              label="Session Type"
              value={
                sessionType === "APPOINTMENT"
                  ? "Appointment"
                  : skipUserSelection
                  ? "Anonymous Walk-in"
                  : "Walk-in"
              }
              icon={sessionType === "APPOINTMENT" ? FiCalendar : FiUser}
            />

            <SummaryRow
              label="Customer"
              value={
                selectedAppointment?.userName ||
                selectedUser?.name ||
                formData.guestCustomer.name ||
                "Anonymous"
              }
              icon={FiUser}
            />

            {skipUserSelection && formData.guestCustomer.phoneNumber && (
              <SummaryRow
                label="Guest Phone"
                value={formData.guestCustomer.phoneNumber}
                icon={FiPhone}
              />
            )}

            <SummaryRow
              label="Duration"
              value={`${formData.expectedDuration} minutes`}
              icon={FiClock}
            />

            <SummaryRow
              label="Admin"
              value={formData.adminName}
              icon={FiUser}
            />

            <div className="pt-3 border-t">
              <SummaryRow
                label="Branch"
                value={formData.branchCode}
                icon={FiMapPin}
              />
            </div>
          </div>
        </div>

        {/* Quick Tips */}
        <div className="bg-gradient-to-br from-primary/10 to-blue-50 rounded-lg p-6 border border-primary/20">
          <h4 className="font-semibold text-primary-dark mb-3 flex items-center gap-2">
            <FiZap className="text-primary" />
            Quick Tips
          </h4>
          <ul className="space-y-2.5 text-sm text-gray-700">
            <li className="flex items-start gap-2">
              <FiCheck className="text-green-600 mt-0.5 flex-shrink-0" />
              <span>Products viewed will be automatically tracked</span>
            </li>
            <li className="flex items-start gap-2">
              <FiCheck className="text-green-600 mt-0.5 flex-shrink-0" />
              <span>Add items to cart as you discuss options</span>
            </li>
            <li className="flex items-start gap-2">
              <FiCheck className="text-green-600 mt-0.5 flex-shrink-0" />
              <span>Session history helps with follow-ups</span>
            </li>
            {skipUserSelection && (
              <li className="flex items-start gap-2">
                <FiCheck className="text-green-600 mt-0.5 flex-shrink-0" />
                <span>Link customer later from session page</span>
              </li>
            )}
          </ul>
        </div>

        {/* Info for Anonymous */}
        {skipUserSelection && (
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <div className="flex items-start gap-2">
              <FiInfo className="text-purple-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-purple-800">
                <p className="font-medium mb-1">Anonymous Session</p>
                <p>
                  Guest details are optional and help with session tracking.
                  This won't create a customer profile.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================
// APPOINTMENT CARD COMPONENT
// ============================================
const AppointmentCard = ({
  appointment,
  onSelect,
  formatDateTime,
  getStatusColor,
}) => {
  return (
    <div
      onClick={onSelect}
      className="bg-white border-2 border-gray-200 rounded-lg p-4 hover:border-primary hover:shadow-lg cursor-pointer transition-all group"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <p className="font-semibold text-gray-900 mb-1.5 group-hover:text-primary transition-colors">
            {appointment.userName}
          </p>
          <Badge variant={getStatusColor(appointment.status)} size="sm">
            {appointment.status}
          </Badge>
        </div>
        <FiChevronRight className="text-gray-400 group-hover:text-primary transition-colors" />
      </div>

      <div className="space-y-2 text-sm text-gray-600 mb-3">
        <div className="flex items-center gap-2">
          <FiPhone className="text-gray-400" size={14} />
          {appointment.userPhone}
        </div>
        <div className="flex items-center gap-2">
          <FiClock className="text-gray-400" size={14} />
          {formatDateTime(appointment.scheduledDateTime)}
        </div>
      </div>

      <div className="pt-3 border-t text-xs text-gray-500 flex items-center justify-between">
        <span>{appointment.type?.replace(/_/g, " ")}</span>
        <span className="font-medium">{appointment.duration} min</span>
      </div>
    </div>
  );
};

// ============================================
// SUMMARY ROW COMPONENT
// ============================================
const SummaryRow = ({ label, value, icon: Icon }) => {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-gray-600 flex items-center gap-2">
        <Icon className="text-gray-400" size={14} />
        {label}
      </span>
      <span className="font-medium text-gray-900">{value}</span>
    </div>
  );
};

export default StartSession;
